import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { Users, Clock, MapPin, Flame, Radio, ArrowRight, Sparkles, Crown } from 'lucide-react';

const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀',
  Hockey: '🏑', Volleyball: '🏐', Pickleball: '🏓', 'Table Tennis': '🏓',
  Squash: '🎾', Athletics: '🏃', Other: '🏟️',
};

const BADGE_STYLES = {
  filling_fast: { color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', icon: Flame, pulse: true },
  live: { color: '#10b981', bg: 'rgba(16,185,129,0.15)', icon: Radio, pulse: true },
  need_players: { color: '#06b6d4', bg: 'rgba(6,182,212,0.15)', icon: Users, pulse: false },
  confirmed: { color: '#a855f7', bg: 'rgba(168,85,247,0.15)', icon: Sparkles, pulse: false },
  completed: { color: '#64748b', bg: 'rgba(100,116,139,0.15)', icon: Clock, pulse: false },
  cancelled: { color: '#94a3b8', bg: 'rgba(148,163,184,0.15)', icon: Clock, pulse: false },
};

export default function ArenaxGameCard({ game, dense = false }) {
  const badge = game.badge || { kind: 'confirmed', text: 'OPEN' };
  const style = BADGE_STYLES[badge.kind] || BADGE_STYLES.confirmed;
  const Icon = style.icon;

  return (
    <Link to={`/arenax/games/${game.id}`}
      className="block rounded-2xl border border-slate-800 hover:border-cyan-500/40 overflow-hidden transition-all hover:-translate-y-1 group"
      style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}
      data-testid={`arenax-game-card-${game.id}`}>
      {/* Cover */}
      <div className={`relative ${dense ? 'aspect-[16/7]' : 'aspect-[16/8]'} overflow-hidden`} style={{
        background: game.venue_cover ? `url(${game.venue_cover}) center/cover` : 'linear-gradient(135deg, #0e7490, #f59e0b)',
      }}>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
        {/* Sport icon top-left */}
        <div className="absolute top-3 left-3 h-9 w-9 rounded-xl flex items-center justify-center text-xl backdrop-blur-md bg-slate-900/60 border border-cyan-500/30">
          {SPORT_ICONS[game.sport] || '🏟️'}
        </div>
        {/* Status badge top-right */}
        <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md backdrop-blur-md border"
          style={{ background: style.bg, borderColor: style.color + '50' }}>
          {style.pulse && <span className="relative h-1.5 w-1.5"><span className="absolute inset-0 rounded-full" style={{ background: style.color }} /><span className="absolute inset-0 rounded-full animate-ping" style={{ background: style.color }} /></span>}
          <Icon className="h-3 w-3" style={{ color: style.color }} />
          <span className="text-[9px] font-bold tracking-wider" style={{ color: style.color }}>{badge.text}</span>
        </div>
      </div>
      {/* Body */}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] tracking-[0.2em] text-cyan-300 font-bold uppercase">{game.skill_level}</span>
          <span className="text-[10px] text-slate-600">•</span>
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">{game.sport}</span>
        </div>
        <div className="text-sm font-bold text-slate-100 mb-2 group-hover:text-cyan-300 transition-colors line-clamp-1">
          {game.venue_name}
        </div>
        <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{game.city}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-amber-400" />{game.start_time}</span>
        </div>

        {/* Players row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Avatar stack mock */}
            <div className="flex -space-x-2">
              {Array.from({ length: Math.min(4, game.participants_count || 0) }).map((_, i) => (
                <div key={i} className="h-6 w-6 rounded-full border-2 border-slate-900 flex items-center justify-center text-[10px] font-bold text-slate-950"
                  style={{ background: ['#06b6d4', '#f59e0b', '#ec4899', '#a855f7'][i] }}>
                  {String.fromCharCode(65 + (i * 5) % 26)}
                </div>
              ))}
              {(game.participants_count || 0) > 4 && (
                <div className="h-6 w-6 rounded-full border-2 border-slate-900 bg-slate-700 flex items-center justify-center text-[9px] font-bold text-slate-300">
                  +{(game.participants_count || 0) - 4}
                </div>
              )}
            </div>
            <span className="text-xs text-slate-400 font-semibold">{game.participants_count}/{game.max_players}</span>
          </div>
          {game.entry_fee_per_player > 0 && (
            <div className="text-right">
              <div className="text-sm font-black text-cyan-300">₹{game.entry_fee_per_player}</div>
              <div className="text-[9px] text-slate-600 tracking-wider uppercase">per player</div>
            </div>
          )}
        </div>

        {game.needs_note && (
          <div className="mt-3 text-[11px] text-amber-300/90 italic">"{game.needs_note}"</div>
        )}
      </div>
    </Link>
  );
}
